/* eslint-disable react-hooks/exhaustive-deps */
import { makeStyles } from '@mui/styles';
import Modal from 'apollo-react/components/Modal';
import _ from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import isBoolean from 'lodash/isBoolean';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import ChartDataModal from './ChartDataModal';
import ConditionModal from './ConditionModal/ConditionModal';
import DatasourceModal from './DatasourceModal';
import {
  dataSourceNew,
  getDatasourceModalTitle,
  isResolver,
  prepareDataSourceToSave,
} from './DatasourceModal/utils';
import ExpressionModal from './ExpressionModal';
import EntityModel from './ExpressionModal/EntityModel';
import ExtraPromptsModal from './ExtraPromptsModal';
import FieldsSelectionModal from './FieldsSelectionModal';
import MappingModal from './MappingModal';
import QueryParamsModal from './QueryParamsModal';
import RuleModal from './RulesModal/RuleModal';
import RuleModalValidation from './RulesModal/RuleModalValidation';
import { MODEL_PICKER_MODAL_STYLES } from '../../../../constants/CommonStylesConstants';
import { useFormSelector } from '../../../../custom-hooks';
import { isFalse, isTrue } from '../../../../utils/commonUtils';
import { updateDatasourceList } from '../../../actions/data-source.actions';
import {
  addChangedDatasource,
  deleteChangedDatasources,
  updateControl,
  updateFormModelByIdAndProp,
} from '../../../actions/editor.actions';
import {
  ACTION_EDITOR,
  ACTION_EDITOR_PROPS,
  CHART_DATA_EDITOR,
  CHART_DATA_EDITOR_PROPS,
  CONTROLLER_TITLE_FORM,
  CONTROLLER_TITLE_PANEL,
  DATA_SOURCE_EDITOR_PROPS,
  DATASOURCE_EDITOR,
  ENTITY_FIELD_PICKER_EDITOR_PROPS,
  ENTITY_MODEL_EDITOR,
  EXPRESSION_EDITOR,
  EXPRESSION_EDITOR_PROPS,
  EXTRA_PROMPTS_EDITOR,
  EXTRA_PROMPTS_EDITOR_PROPS,
  MAPPING_EDITOR,
  MAPPING_EDITOR_PROPS,
  QUERY_PARAM_EDITOR,
  QUERY_PARAM_EDITOR_PROPS,
  RULE_EDITOR,
  RULE_EDITOR_PROPS,
  SELECTED_FIELDS_EDITOR,
  SELECTED_FIELDS_EDITOR_PROPS,
  VALIDATION_EDITOR,
  VALIDATION_EDITOR_PROPS,
} from '../../../constants/editor.constants';
import {
  BUTTON_DEFAULT_LABELS,
  FORM_MODEL_ATTRIBUTES,
} from '../../../constants/form-manager.constants';
import {
  getDataSources,
  getFormInfo,
  getFormModel,
  getTranslations,
} from '../../../selectors/common.selectors';
import {
  getSelectedDataSource,
  getSelectedDataSourceFromModel,
} from '../../../selectors/editor.selectors';
import {
  getEntityFromEntityDefinition,
  getEvaluatedExpression,
  getModelById,
  getPropByModel,
  getPropTranslationSelector,
} from '../../../selectors/form-manager.selectors';
import {
  divideUpdateAndInteractionRules,
  getControlModelWithAllUpdateRules,
} from '../../../utils/actions/rule.actions.utils';
import {
  changeDataSource,
  findParentByType,
  formatControlTypeName,
  formatStringForRowTitleRepeatingPanel,
  getDatasourceFields,
  getLastModifiedDateContainer,
} from '../../../utils/editor.utils';
import { equalityFn, updateRulesScripts } from '../../../utils/form-manager.utils';

const styles = {
  modal: {
    minWidth: '80%',

    '& [class*="MuiDialogContent-root"]': {
      paddingTop: '0 !important',
    },
  },
  entityModelModal: MODEL_PICKER_MODAL_STYLES,
};

const getFormName = (formComponent) => {
  const { formName } = formComponent ?? {};
  return formName;
};

const getEvaluationPropValue = (property) => {
  return property && !property.includes('_eval_error_') ? property : '';
};

export const PropertyComponentModal = ({
  open,
  onClose,
  controlId,
  name,
  label,
  dataSourceFromStore,
  dataSourceEditAction,
  setDataSourceEditAction,
  setActualValue,
  changeDataSourceFieldsInModel,
  isDataSourceViewer,
  isReadOnly,
  apiConfigurations,
}) => {
  const [modalTitle, setModalTitle] = useState('');
  const [dataSourceObject, setDatasourceObject] = useState(dataSourceNew);
  const [filterDataSourceMode, setFilterDataSourceMode] = useState(false);
  const [dataSourceFilterColumns, setDataSourceFilterColumns] = useState([]);
  const [isDatasourceValid, setIsDatasourceValid] = useState(false);
  const [allowFilterEdit, setAllowFilterEdit] = useState(true);
  const [buttonList, setButtonList] = useState([
    {},
    { label: 'Save', onClick: () => saveChanges({ expressionValue }), 'ui-ta': 'SaveBtn' },
  ]);
  const dispatch = useDispatch();
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const fieldName = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.FIELD_NAME,
  });
  const entityName = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.ENTITY_NAME,
  });
  const expressionSelectorProps = { id: controlId, index: [0], entityName };
  const title = useFormSelector(getEvaluatedExpression, {
    ...expressionSelectorProps,
    prop: FORM_MODEL_ATTRIBUTES.TITLE,
  });
  const rowTitle = formatStringForRowTitleRepeatingPanel(
    useFormSelector(getEvaluatedExpression, {
      ...expressionSelectorProps,
      prop: FORM_MODEL_ATTRIBUTES.ROW_TITLE,
    })
  );

  const labelProperty = useFormSelector(getEvaluatedExpression, {
    ...expressionSelectorProps,
    prop: FORM_MODEL_ATTRIBUTES.LABEL,
  });
  const entityNameTranslated = useFormSelector(getPropTranslationSelector, {
    entityId: fieldName,
    entityName,
  });
  const controlType = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.CONTROL_TYPE,
  });
  const buttonType = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.BUTTON_TYPE,
  });
  const actualSelectedProp = useFormSelector(getPropByModel, {
    id: controlId,
    prop: label.toLowerCase(),
  });
  const selectedEntity = useFormSelector(getEntityFromEntityDefinition, { entityName });

  const translations = useFormSelector(getTranslations);

  const { Entity } = translations;

  const selectValuesFromEntity = useMemo(
    () =>
      Object.keys(selectedEntity?.columns ?? [])
        .filter((field) => !['WARNINGS', 'ERRORS'].includes(field))
        .map((field) => {
          const translatedFieldName = Entity[selectedEntity?.columns[field]?.properties.Caption];
          const fullFieldName = `${selectedEntity.properties.TableName}.${field}`;
          //convert the values to this type {|CAPA Type|V_SS_CAPA.CAPA_TYPE|}
          return {
            value: `{|${translatedFieldName}|${fullFieldName}|}`,
            label: `${translatedFieldName} (${field})`,
          };
        }),
    [Entity, selectedEntity]
  );

  const component = useFormSelector(getModelById, { id: controlId });
  const { parent } = component ?? {};

  const clonedComponent = cloneDeep(component);
  const [currentControlModel, setCurrentControlModel] = useState(
    name === 'update' ? getControlModelWithAllUpdateRules(clonedComponent) : clonedComponent
  );
  const parentComponent = useFormSelector(getModelById, { id: parent ?? '' });
  const subFormName = getFormName(parentComponent);
  const formModel = useFormSelector(getFormModel);
  const formInfo = useFormSelector(getFormInfo);
  const formName = getFormName(formInfo);
  const selectedDataSource = useFormSelector(getSelectedDataSource, {
    prop: FORM_MODEL_ATTRIBUTES.DATA_SOURCE_KEY,
  });
  const selectedDataSourceFromStore = useFormSelector(
    getSelectedDataSourceFromModel,
    {
      dataSourceFromStore,
    },
    equalityFn
  );
  const dataSources = useFormSelector(getDataSources);
  const filterConditions =
    dataSourceEditAction === 'edit'
      ? selectedDataSource?.filter?.Conditions ?? selectedDataSourceFromStore?.filter?.Conditions
      : dataSourceNew?.dataSourceProperties?.filter?.Conditions;
  const [tempFilterConditions, setTempFilterConditions] = useState(filterConditions ?? []);
  const [currentControlMapping, setCurrentControlMapping] = useState(currentControlModel[name]);
  const [currentQueryParams, setCurrentQueryParams] = useState(
    currentControlModel[name]?.paramListItems
  );
  const [currentChartData, setCurrentChartData] = useState(currentControlModel.chartData);

  const sourceType = currentControlModel[FORM_MODEL_ATTRIBUTES.DATA_LOOK_UP_TYPE];
  const sourceIdentifier = currentControlModel[FORM_MODEL_ATTRIBUTES.DATA_OBJECT];

  const propValueStore = useFormSelector(getPropByModel, { id: controlId, prop: name });
  const propValueTemp = isObject(propValueStore)
    ? JSON.stringify(propValueStore, undefined, 4)
    : isString(propValueStore)
    ? propValueStore.replace(/eval\|\|/g, '')
    : isBoolean(propValueStore)
    ? propValueStore
    : '';

  const propValue =
    propValueTemp && isString(propValueTemp)
      ? updateRulesScripts(propValueTemp, true)
      : propValueTemp;

  const [expressionValue, setExpressionValue] = useState(propValue ?? '');

  const type = VALIDATION_EDITOR_PROPS.includes(name)
    ? VALIDATION_EDITOR
    : RULE_EDITOR_PROPS.includes(name)
    ? RULE_EDITOR
    : EXPRESSION_EDITOR_PROPS.includes(name)
    ? EXPRESSION_EDITOR
    : ACTION_EDITOR_PROPS.includes(name)
    ? ACTION_EDITOR
    : DATA_SOURCE_EDITOR_PROPS.includes(name)
    ? DATASOURCE_EDITOR
    : MAPPING_EDITOR_PROPS.includes(name)
    ? MAPPING_EDITOR
    : QUERY_PARAM_EDITOR_PROPS.includes(name)
    ? QUERY_PARAM_EDITOR
    : CHART_DATA_EDITOR_PROPS.includes(name)
    ? CHART_DATA_EDITOR
    : SELECTED_FIELDS_EDITOR_PROPS.includes(name)
    ? SELECTED_FIELDS_EDITOR
    : ENTITY_FIELD_PICKER_EDITOR_PROPS.includes(name)
    ? ENTITY_MODEL_EDITOR
    : EXTRA_PROMPTS_EDITOR_PROPS.includes(name)
    ? EXTRA_PROMPTS_EDITOR
    : '';

  const titleModalHandler = () => {
    if (type === DATASOURCE_EDITOR) {
      return getDatasourceModalTitle({ dataSourceEditAction, propValue });
    }
    let newTitle = '';
    if (CONTROLLER_TITLE_PANEL.includes(controlType)) {
      if (controlType.includes('ssButton')) {
        newTitle = `${formatControlTypeName(controlType)}${
          BUTTON_DEFAULT_LABELS[buttonType] ? ` - ${BUTTON_DEFAULT_LABELS[buttonType]}` : ''
        } - ${label}`;
      } else {
        let propValue = title;
        if (controlType.includes('ssSingleTab')) {
          propValue = labelProperty;
        }
        if (controlType.includes('ssRepeatingPanel')) {
          propValue = rowTitle;
        }
        const propEvaluationValue = getEvaluationPropValue(propValue);
        newTitle = `${formatControlTypeName(controlType)} ${
          propEvaluationValue ? `- ${propEvaluationValue}` : ''
        } - ${label}`;
      }
    } else if (CONTROLLER_TITLE_FORM.includes(controlType)) {
      const tempTitle = parent === 'root' ? formName : subFormName;
      newTitle = tempTitle ? `${type} (${tempTitle})` : `${type} (${label})`;
    } else {
      newTitle = entityNameTranslated
        ? `${type} (${entityNameTranslated} - ${label})`
        : `${type} (${label})`;
    }

    return newTitle;
  };

  const revertToActualValue = (actualValue) => {
    if (!setActualValue) {
      return;
    }

    if (isTrue(actualValue)) {
      return setActualValue(true);
    } else if (isFalse(actualValue)) {
      return setActualValue(false);
    } else if (actualValue === '') {
      return setActualValue(actualValue);
    }
  };

  const onCloseHandler = () => {
    if (filterDataSourceMode) {
      setFilterDataSourceMode(false);
      return;
    }
    revertToActualValue(actualSelectedProp);
    setCurrentControlModel(clonedComponent);
    onClose();
  };

  useEffect(() => {
    const fields = getDatasourceFields(selectedDataSource ?? selectedDataSourceFromStore);
    setDataSourceFilterColumns(fields);
  }, [JSON.stringify(selectedDataSource), JSON.stringify(selectedDataSourceFromStore)]);

  useEffect(() => {
    if (dataSourceEditAction === 'edit') {
      setDatasourceObject({
        dataSourceKey:
          propValue ||
          selectedDataSource?.dataSourceKey ||
          selectedDataSource?.dataSourceName ||
          dataSourceFromStore,
        dataSourceProperties: {
          ...selectedDataSource,
          ...selectedDataSourceFromStore,
        },
      });
    } else if (dataSourceEditAction === 'new') {
      setDatasourceObject(cloneDeep(dataSourceNew));
    }
  }, []);

  useEffect(() => {
    if (type === DATASOURCE_EDITOR && !filterDataSourceMode) {
      setButtonList([
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: () => {
            dataSourceCancel();
          },
          'ui-ta': 'CancelBtn',
        },
        {
          label: 'Delete',
          variant: 'secondary',
          disabled:
            dataSourceEditAction !== 'edit' ||
            filterDataSourceMode ||
            isResolver(selectedDataSource) ||
            isResolver(selectedDataSourceFromStore),
          onClick: () => {
            // eslint-disable-next-line no-restricted-globals
            const confirmMessage = confirm('Do you want to delete this datasource?');
            if (confirmMessage) {
              setDataSourceEditAction('delete');
              saveChanges({ mode: 'delete' });
            }
          },
          'ui-ta': 'DeleteBtn',
        },
        {
          label: 'Filter',
          variant: 'secondary',
          disabled:
            !allowFilterEdit ||
            dataSourceFilterColumns?.length === 0 ||
            filterDataSourceMode ||
            (dataSourceEditAction === 'edit' &&
              (isResolver(selectedDataSource) || isResolver(selectedDataSourceFromStore))),
          onClick: () => setFilterDataSourceMode(true),
          'ui-ta': 'FilterBtn',
        },
        {
          label: 'Save',
          disabled:
            !isDatasourceValid ||
            (dataSourceEditAction === 'edit' &&
              (isResolver(selectedDataSource) || isResolver(selectedDataSourceFromStore))),
          onClick: () => {
            changeDataSource({
              dataSourceObject,
              property: 'filter.Conditions',
              propertyValue: tempFilterConditions,
              setDatasourceObject,
            });
            saveChanges();
          },
          'ui-ta': 'SaveBtn',
        },
      ]);
    } else if (type === ENTITY_MODEL_EDITOR) {
      setButtonList([
        {
          label: 'Cancel',
          onClick: () => {
            onCloseHandler();
          },
          'ui-ta': 'CancelBtn',
        },
      ]);
      // temporary solution to the problem of saving changes
    } else if (filterDataSourceMode) {
      setButtonList([
        {
          label: 'Cancel',
          onClick: () => {
            setFilterDataSourceMode(false);
          },
          'ui-ta': 'CancelBtn',
        },
        {
          label: 'Save',
          onClick: () => {
            saveFilterChanges();
          },
          'ui-ta': 'SaveBtn',
        },
      ]);
    } else if (isReadOnly) {
      setButtonList([{ label: 'Cancel', onClick: () => onCloseHandler(), 'ui-ta': 'CancelBtn' }]);
    } else {
      setButtonList([
        {
          label: 'Cancel',
          onClick: () => {
            onCloseHandler();
          },
          'ui-ta': 'CancelBtn',
        },
        { label: 'Save', onClick: () => saveChanges({ expressionValue }), 'ui-ta': 'SaveBtn' },
      ]);
    }
  }, [
    dataSourceFilterColumns,
    isDatasourceValid,
    filterDataSourceMode,
    dataSourceEditAction,
    JSON.stringify(currentControlModel),
    currentControlMapping,
    currentQueryParams,
    currentChartData,
    isReadOnly,
    expressionValue,
  ]);

  useEffect(() => {
    setModalTitle(titleModalHandler());
    setCurrentControlModel(
      name === 'update' ? getControlModelWithAllUpdateRules(clonedComponent) : clonedComponent
    );
  }, [JSON.stringify(clonedComponent)]);

  const saveFilterChanges = () => {
    const _tempFilterConditions = Array.isArray(currentControlModel[name]?.Conditions)
      ? currentControlModel[name].Conditions
      : currentControlModel[name] === ''
      ? []
      : tempFilterConditions;

    setFilterDataSourceMode(false);
    setTempFilterConditions(_tempFilterConditions);
  };

  const saveChanges = ({ mode, expressionValue } = {}) => {
    switch (type) {
      case EXPRESSION_EDITOR: {
        const value = expressionValue;
        const storeParsedValue = value && !value.includes('eval||') ? `eval||${value}` : value;
        const preProcessedRules =
          storeParsedValue && isString(storeParsedValue)
            ? updateRulesScripts(storeParsedValue)
            : storeParsedValue;
        dispatch(updateFormModelByIdAndProp(controlId, name, preProcessedRules));
        onClose();
        return;
      }

      case DATASOURCE_EDITOR: {
        const containerFormNameForNewDS = findParentByType(
          formModel,
          controlId,
          formInfo,
          'ssSubForm'
        );
        const formLastModified = getLastModifiedDateContainer(
          formModel,
          dataSourceObject?.dataSourceProperties?.containerFormName,
          formInfo
        );
        const formLastModifiedForNewDS = getLastModifiedDateContainer(
          formModel,
          containerFormNameForNewDS,
          formInfo
        );

        if (mode === 'delete' || dataSourceEditAction === 'delete') {
          const deletedDatasource = {
            dataSourceKey:
              propValue ||
              selectedDataSource?.dataSourceKey ||
              selectedDataSource?.dataSourceName ||
              dataSourceFromStore,
            containerFormName: dataSourceObject?.dataSourceProperties?.containerFormName,
            formLastModified,
            changeType: 'DELETED',
          };
          dispatch(
            addChangedDatasource({
              dataSource: deletedDatasource,
              controlId: !isDataSourceViewer && controlId,
              datasourceAction: 'delete',
            })
          );
          dispatch(
            deleteChangedDatasources({ dataSourcesKeys: [deletedDatasource?.dataSourceKey] })
          );
          if (!isDataSourceViewer) {
            changeDataSourceFieldsInModel({ needPersistUserAction: false });
          }
          setDatasourceObject(dataSourceNew);
          setDataSourceEditAction('new');
        } else if (mode === 'new' || dataSourceEditAction === 'new') {
          const cleanDataSource = prepareDataSourceToSave(dataSourceObject);
          if (isDatasourceValid) {
            dispatch(
              addChangedDatasource({
                dataSource: {
                  ...cleanDataSource,
                  changeType: 'ADDED',
                  containerFormName: containerFormNameForNewDS,
                  formLastModified: formLastModifiedForNewDS,
                },
                datasourceAction: 'add',
                controlId: !isDataSourceViewer && controlId,
              })
            );
            cleanDataSource.dataSourceProperties.containerFormName = containerFormNameForNewDS;
            dispatch(updateDatasourceList({ datasource: cleanDataSource }));
            if (!isDataSourceViewer) {
              changeDataSourceFieldsInModel({
                dataSourceKey: cleanDataSource?.dataSourceKey,
                needPersistUserAction: false,
              });
            }
            setDatasourceObject(dataSourceNew);
            setDataSourceEditAction('new');
          }
        } else if (mode === 'edit' || dataSourceEditAction === 'edit') {
          const cleanDataSource = prepareDataSourceToSave(
            dataSourceObject,
            propValue || dataSourceFromStore
          );

          if (isDatasourceValid) {
            dispatch(
              addChangedDatasource({
                dataSource: {
                  ...cleanDataSource,
                  changeType: dataSourceObject?.dataSourceProperties?.isNew ? 'ADDED' : 'MODIFIED',
                  containerFormName: dataSourceObject?.dataSourceProperties?.containerFormName,
                  formLastModified,
                },
                datasourceAction: 'edit',
                controlId: !isDataSourceViewer && controlId,
              })
            );
            dispatch(updateDatasourceList({ datasource: cleanDataSource }));
            if (!isDataSourceViewer) {
              changeDataSourceFieldsInModel({
                dataSourceKey: cleanDataSource?.dataSourceKey,
                needPersistUserAction: false,
              });
            }
            setDatasourceObject(dataSourceNew);
            setDataSourceEditAction('new');
          }
        }
        onClose();
        return;
      }

      case RULE_EDITOR:
      case VALIDATION_EDITOR:
      case ACTION_EDITOR:
      case SELECTED_FIELDS_EDITOR:
      case EXTRA_PROMPTS_EDITOR:
        revertToActualValue(actualSelectedProp);
        dispatch(
          updateControl({
            id: controlId,
            controlChanges:
              type === ACTION_EDITOR
                ? divideUpdateAndInteractionRules(currentControlModel)
                : currentControlModel,
          })
        );
        onClose();
        return;

      case MAPPING_EDITOR: {
        revertToActualValue(actualSelectedProp);
        dispatch(updateFormModelByIdAndProp(controlId, name, currentControlMapping));
        onClose();
        return;
      }

      case ENTITY_MODEL_EDITOR: {
        revertToActualValue(actualSelectedProp);

        const expression = expressionValue ? `eval||${expressionValue}` : expressionValue;
        dispatch(updateFormModelByIdAndProp(controlId, name, expression));
        onClose();
        return;
      }

      case QUERY_PARAM_EDITOR: {
        dispatch(
          updateFormModelByIdAndProp(
            controlId,
            name,
            _.isEmpty(currentQueryParams) ? '' : { paramListItems: currentQueryParams }
          )
        );
        onClose();
        return;
      }

      case CHART_DATA_EDITOR: {
        dispatch(updateFormModelByIdAndProp(controlId, name, currentChartData));
        onClose();
        return;
      }

      default: {
        return;
      }
    }
  };

  const updateExpressionTextFieldFromOutside = (updateValue) => {
    const updatedValue = updateValue(expressionValue);
    setExpressionValue(updatedValue);
  };

  const updateExpression = ({ target }) => {
    setExpressionValue(target.value);
  };

  const onEntityFieldChange = (selectedEntityField = '') => {
    const expressionValue = selectedEntityField && `eval||${selectedEntityField}`;

    saveChanges({ expressionValue });
  };

  const componentType = (type) => {
    switch (type) {
      case EXPRESSION_EDITOR:
        return (
          <ExpressionModal
            onChange={updateExpressionTextFieldFromOutside}
            expression={expressionValue}
            onBlurEditor={updateExpression}
          />
        );
      case ACTION_EDITOR:
        return (
          <RuleModal
            controlId={controlId}
            name={name}
            currentControlModel={currentControlModel}
            setCurrentControlModel={setCurrentControlModel}
          />
        );

      case DATASOURCE_EDITOR:
        return (
          <DatasourceModal
            name={name}
            controlId={controlId}
            dataSources={dataSources}
            dataSourceObject={dataSourceObject}
            setAllowFilterEdit={setAllowFilterEdit}
            setDatasourceObject={setDatasourceObject}
            setIsDatasourceValid={setIsDatasourceValid}
            dataSourceEditAction={dataSourceEditAction}
            setTempFilterConditions={setTempFilterConditions}
            setDataSourceEditAction={setDataSourceEditAction}
            setDataSourceFilterColumns={setDataSourceFilterColumns}
          />
        );

      case VALIDATION_EDITOR:
        return (
          <RuleModalValidation
            controlId={controlId}
            name={name}
            currentControlModel={currentControlModel}
            setCurrentControlModel={setCurrentControlModel}
          />
        );

      case RULE_EDITOR:
        return (
          <ConditionModal
            name={name}
            isDataSource={
              name === FORM_MODEL_ATTRIBUTES.DATA_SOURCE_FILTER ||
              (name === FORM_MODEL_ATTRIBUTES.DATA_SOURCE_KEY && filterDataSourceMode) ||
              name === FORM_MODEL_ATTRIBUTES.FILTER
            }
            newDataSourceObject={{
              dataSourceKey: dataSourceObject?.dataSourceKey,
              ...dataSourceObject,
              dataSourceProperties: {
                ...dataSourceObject.dataSourceProperties,
              },
            }}
            sourceType={sourceType}
            sourceIdentifier={sourceIdentifier}
            currentControlModel={currentControlModel}
            setCurrentControlModel={setCurrentControlModel}
            dataSourceFilterColumns={dataSourceFilterColumns}
            tempFilterConditions={tempFilterConditions}
            isReadOnly={isReadOnly}
            controlType={controlType}
            selectValuesFromEntity={selectValuesFromEntity}
          />
        );

      case MAPPING_EDITOR:
        return (
          <MappingModal
            controlId={controlId}
            name={name}
            currentControlModel={currentControlModel}
            setCurrentControlMapping={setCurrentControlMapping}
            currentControlMapping={currentControlMapping}
            isReadOnly={isReadOnly}
            dataSources={dataSources}
            apiConfigurations={apiConfigurations}
          />
        );

      case QUERY_PARAM_EDITOR:
        return (
          <QueryParamsModal
            controlId={controlId}
            name={name}
            currentControlModel={currentControlModel}
            currentQueryParams={currentQueryParams}
            setCurrentQueryParams={setCurrentQueryParams}
          />
        );

      case CHART_DATA_EDITOR:
        return (
          <ChartDataModal
            currentChartData={currentChartData}
            setCurrentChartData={setCurrentChartData}
          />
        );

      case SELECTED_FIELDS_EDITOR:
        return (
          <FieldsSelectionModal
            controlId={controlId}
            name={name}
            currentControlModel={currentControlModel}
            setCurrentControlModel={setCurrentControlModel}
          />
        );

      case EXTRA_PROMPTS_EDITOR:
        return (
          <ExtraPromptsModal
            controlId={controlId}
            name={name}
            currentControlModel={currentControlModel}
            setCurrentControlModel={setCurrentControlModel}
          />
        );

      case ENTITY_MODEL_EDITOR:
        return (
          <EntityModel
            action
            change={onEntityFieldChange}
            translation
            showSearch
            showTitle={false}
          />
        );

      default:
        return null;
    }
  };

  const dataSourceCancel = () => {
    setDatasourceObject(dataSourceNew);
    onClose();
  };

  return (
    <Modal
      open={open}
      variant="default"
      className={type === ENTITY_MODEL_EDITOR ? classes.entityModelModal : classes.modal}
      onClose={() => onCloseHandler()}
      title={modalTitle}
      buttonProps={buttonList}
      disableBackdropClick
      disableEscapeKeyDown
    >
      {filterDataSourceMode ? componentType(RULE_EDITOR) : componentType(type)}
    </Modal>
  );
};
